import * as React from 'react';
import type { AvailabilityImportPreviewResponse, AvailabilityProfile } from '@ooh/contracts';
import {
  buildDisplayRows,
  computeAllRowsUnmatched,
  indexRematchedRows,
  mergeRematchedRows,
} from '../lib/build-display-rows';
import { useRematchedRows } from './use-rematched-rows';

/** The interpreted rows the preview shows for the current (edited) profile, with the debounced rematch overlaid. */
export function usePreviewDisplayRows(
  jobId: number,
  initialData: AvailabilityImportPreviewResponse,
  profile: AvailabilityProfile,
) {
  const { rows: rematchedRows, isError: rematchFailed } = useRematchedRows(jobId, profile);
  const rematchByRowNumber = React.useMemo(
    () => indexRematchedRows(rematchedRows),
    [rematchedRows],
  );
  const effectiveRows = React.useMemo(
    () => mergeRematchedRows(initialData.rows, rematchByRowNumber),
    [initialData.rows, rematchByRowNumber],
  );
  const { sampleRows } = initialData;
  const displayRows = React.useMemo(
    () => buildDisplayRows(profile, sampleRows, effectiveRows, rematchByRowNumber),
    [profile, sampleRows, effectiveRows, rematchByRowNumber],
  );
  const allRowsUnmatched = computeAllRowsUnmatched(displayRows, sampleRows.length);

  return { displayRows, allRowsUnmatched, rematchFailed };
}
